import {
	Carousel,
	ChevronLeftIcon,
	ChevronRightIcon,
	cn,
	ThreeStarsIcon,
} from "@au-schein-de/au-ui-library";
import type { FormatedProductPrice, ProductNoEdibleInfo } from "@dransay/api";
import { cva } from "class-variance-authority";
import type { EmblaCarouselType } from "embla-carousel";
import { forwardRef, useCallback, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { calculateProductsComparisonDiscount } from "~/features/price-comparison/price-comparison-discounts";
import { ProductCard } from "~/features/products/card/product-card.component";
import { ExclusiveCarouselHeader } from "~/features/products/exclusive-products/exclusive-carousel-header.component";
import {
	EXCLUSIVE_PDP_CAROUSEL_CONTENT_CLASS,
	EXCLUSIVE_PDP_CAROUSEL_INSET_CLASS,
	EXCLUSIVE_PDP_CAROUSEL_ITEM_CLASS,
	EXCLUSIVE_PDP_CAROUSEL_OPTS,
} from "~/features/products/exclusive-products/exclusive-carousel-nav-button-styles";
import { ExclusiveCarouselNavigation } from "~/features/products/exclusive-products/exclusive-carousel-navigation.component";
import { ExclusiveProductsBackgroundPattern } from "~/features/products/exclusive-products/exclusive-products-background-pattern.component";
import { ExclusiveProductsCarouselScrollBar } from "~/features/products/exclusive-products/exclusive-products-carousel-scrollbar.component";
import { useCurrency } from "~/hooks/use-currency-price.hook";
import { useMarkFavorites } from "~/hooks/use-mark-favorites.hook";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import {
	promotionClickedEvent,
	promotionViewedEvent,
} from "~/tooling/telemetry/events";
import { getProductDetailsUrl } from "~/utils/routes";
import { IntersectionObserverComponent } from "./intersection-observer.component";

const carouselItemVariants = cva(
	`max-[375px]:basis-4/5 basis-[40%] min-w-[164px] max-w-[164px] md:max-w-full md:min-w-[203px] md:basis-1/3 2xl:basis-1/6 ${EXCLUSIVE_PDP_CAROUSEL_ITEM_CLASS}`,
	{
		variants: {
			variant: {
				listing: "lg:basis-1/4",
				productPage: "lg:basis-1/6",
			},
		},
		defaultVariants: {
			variant: "listing",
		},
	},
);

interface CarouselProps {
	className?: string;
	products: FormatedProductPrice[];
	variant?: "listing" | "productPage";
	promotedProduct?: FormatedProductPrice;
	mobileTitle?: string;
	desktopTitle?: string;
}

const getPromotionId = (suffix: string) => `recommendations-slider-${suffix}`;
const PROMOTION_NAME = "Recommendations Slider";

export const ProductCarousel = forwardRef<HTMLDivElement, CarouselProps>(
	(
		{
			products,
			className,
			variant = "listing",
			promotedProduct,
			mobileTitle,
			desktopTitle,
		},
		ref,
	) => {
		const { t } = useTranslation("product");
		const location = useLocation();
		const { isFavorite } = useMarkFavorites();
		const [carouselApi, setCarouselApi] = useState<EmblaCarouselType>();
		const exclusiveCarouselId = useId();

		const getRecommendedProductUrl = useCallback(
			(item: ProductNoEdibleInfo) => {
				const urlBase = getProductDetailsUrl(item.id, item.name, item.kind);
				const queryParams = new URLSearchParams(location.search);
				queryParams.delete("search");

				const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";

				return `${urlBase}${qs}`;
			},
			[location.search],
		);

		const getPriceComparisonDiscount = useCallback(
			(item: FormatedProductPrice) => {
				return calculateProductsComparisonDiscount(
					item.priceComparisonSet?.original,
					item.priceComparisonSet?.comparison,
				);
			},
			[],
		);

		const currency = useCurrency();

		const onClick = useCallback(
			(product: FormatedProductPrice, index: number) => {
				captureEvent(
					promotionClickedEvent({
						promotionId: getPromotionId(variant),
						name: PROMOTION_NAME,
						position: index,
						products: [product],
						currency: currency,
					}),
				);
			},
			[variant, currency],
		);

		const onIntersection = useCallback(
			(product: FormatedProductPrice, index: number) => {
				captureEvent(
					promotionViewedEvent({
						promotionId: getPromotionId(variant),
						name: PROMOTION_NAME,
						position: index,
						products: [product],
						currency,
					}),
				);
			},
			[variant, currency],
		);

		const renderCarouselItem = useCallback(
			(product: FormatedProductPrice, index: number) => (
				<IntersectionObserverComponent
					key={`product-carousel-item-${product.id}`}
					onIntersection={() => onIntersection(product, index)}
				>
					<Carousel.Item
						className={carouselItemVariants({ variant })}
						full={false}
						data-testid={product.promoted ? "promoted-product" : undefined}
						onClick={() => onClick(product, index)}
					>
						<ProductCard
							idSuffix="recommended"
							border="light"
							size="sm"
							variant="carouselItem"
							badges={variant === "productPage" ? ["exclusive"] : []}
							forceExclusiveBadge={variant === "productPage"}
							exclusivePdpCard={variant === "productPage"}
							url={getRecommendedProductUrl(product)}
							priceComparisonDiscount={getPriceComparisonDiscount(product)}
							listIndex={index}
							isFavoriteOverride={isFavorite(product.id)}
							{...product}
						/>
					</Carousel.Item>
				</IntersectionObserverComponent>
			),
			[
				getPriceComparisonDiscount,
				getRecommendedProductUrl,
				variant,
				onClick,
				onIntersection,
				isFavorite,
			],
		);

		// Filter out promoted product from regular products
		// to avoid possibly showing the same product twice
		const filteredProducts = useMemo(() => {
			if (!promotedProduct) return products;
			return products.filter((p) => p.id !== promotedProduct.id);
		}, [products, promotedProduct]);

		const displayedProducts = useMemo(
			() => [
				...(promotedProduct ? [promotedProduct] : []),
				...filteredProducts,
			],
			[promotedProduct, filteredProducts],
		);

		if (products.length < 4) {
			return null;
		}

		const isProductPage = variant === "productPage";
		const listingTitle = `${t("curated.exclusive.title")} (${displayedProducts.length})`;
		const carouselOpts = isProductPage
			? EXCLUSIVE_PDP_CAROUSEL_OPTS
			: { align: "start" as const, loop: true };
		return (
			<Carousel.Root
				ref={ref}
				opts={carouselOpts}
				className={cn(
					isProductPage
						? "mb-0 md:mb-6 w-full overflow-hidden bg-gray-200 pt-4 pb-6 md:rounded-lg md:py-6"
						: "-ml-4 mb-6 w-[calc(100%+32px)] overflow-hidden bg-gray-200 p-4 md:ml-0 md:w-full md:rounded-lg md:px-10 md:py-6",
					className,
				)}
				buttonPosition="static"
				data-testid="product-carousel"
				setApi={isProductPage ? setCarouselApi : undefined}
			>
				{isProductPage && (
					<ExclusiveProductsBackgroundPattern productCount={products.length} />
				)}
				{isProductPage ? (
					<div className={EXCLUSIVE_PDP_CAROUSEL_INSET_CLASS}>
						<ExclusiveCarouselHeader
							mobileTitle={mobileTitle ?? ""}
							desktopTitle={desktopTitle ?? ""}
							navigation={<ExclusiveCarouselNavigation api={carouselApi} />}
						/>
					</div>
				) : (
					<div className="relative mb-4 flex items-center justify-between gap-2 md:mb-3.5">
						<div className="text-body-lg-emphasis md:text-h5 text-gray-700 flex space-x-4 items-center">
							<span className="max-[375px]:hidden block">
								<ThreeStarsIcon size={10} />
							</span>
							{listingTitle}
						</div>
						<div className="flex shrink-0 gap-2">
							<Carousel.Previous
								className="h-10 w-10 hover:bg-gray-100 border-gray-200 text-gray-700"
								icon={
									<ChevronLeftIcon.Raw className="text-gray-700 w-6 h-6 -ml-[3px]" />
								}
							/>
							<Carousel.Next
								className="h-10 w-10 hover:bg-gray-100 border-gray-200 text-gray-700"
								icon={
									<ChevronRightIcon.Raw className="text-gray-700 w-6 h-6 ml-[3px]" />
								}
							/>
						</div>
					</div>
				)}
				<div className="relative flex align-center">
					<Carousel.Content
						id={isProductPage ? exclusiveCarouselId : undefined}
						className={cn(
							"items-start",
							isProductPage ? EXCLUSIVE_PDP_CAROUSEL_CONTENT_CLASS : "-ml-1",
						)}
					>
						{displayedProducts.map((product, index) =>
							renderCarouselItem(product, index),
						)}
					</Carousel.Content>
				</div>
				{isProductPage && (
					<div className={EXCLUSIVE_PDP_CAROUSEL_INSET_CLASS}>
						<ExclusiveProductsCarouselScrollBar
							api={carouselApi}
							controlsId={exclusiveCarouselId}
							className="mt-4"
						/>
					</div>
				)}
			</Carousel.Root>
		);
	},
);
